<template>
	<div class="pagination">
		<!-- <div class="results">{{ limit }} entries per page</div> -->
		<div class="pages">
			<!-- Prev -->
			<router-link
				v-if="prev"
				:to="prev"
				class="pages__sq arrow arrow--prev arrow--enable"
			></router-link>
			<div v-else class="pages__sq arrow arrow--prev arrow--disable"></div>
			<!-- Pages -->
			<template v-for="p in pagesSubset">
				<div v-if="p.dot" :key="p.dot" class="pages__sq page page--dot"></div>
				<div
					v-else-if="p.active"
					:key="p.number"
					class="pages__sq page page--active"
				>
					{{ p.number }}
				</div>
				<router-link
					v-else
					:key="p.number"
					:to="getPage(p.number)"
					class="pages__sq page page--link"
					>{{ p.number }}</router-link
				>
			</template>
			<!-- Next -->
			<router-link
				v-if="next"
				:to="next"
				class="pages__sq arrow arrow--next arrow--enable"
			></router-link>
			<div v-else class="pages__sq arrow arrow--next arrow--disable"></div>
		</div>
	</div>
</template>

<script>
const MAX_PAGES = 3

const fields = {
	limit: { key: '_limit', default: 10 },
	start: { key: '_start', default: 0 }
}

export default {
	name: 'strapi-pagination',
	props: {
		count: {
			type: Number,
			default: 0
		}
	},
	computed: {
		limit() {
			return this.getField('limit')
		},
		start() {
			return this.getField('start')
		},
		page() {
			return Math.floor(this.start / this.limit) + 1
		},
		pages() {
			return Math.ceil(this.count / this.limit)
		},
		prev() {
			return this.page > 1 ? this.getPage(this.page - 1) : null
		},
		next() {
			return this.page < this.pages ? this.getPage(this.page + 1) : null
		},
		pagesSubset() {
			const size = Math.min(this.pages, MAX_PAGES)
			let pages = []
			let start

			// Determine first page in subset
			if (!this.prev) start = 1
			else if (!this.next) start = this.pages - size + 1
			else start = this.page - 1

			// Add first page jump
			if (start > 2) {
				pages.push({ number: 1, is: 'router-link' }, { dot: 'a' })
			}

			// Create pages array
			for (let i = 0; i < size; i++) {
				const number = start + i
				const active = number === this.page
				pages.push({ number, active })
			}

			// Add last page jump
			if (start + size < this.pages) {
				pages.push({ dot: 'b' }, { number: this.pages, is: 'router-link' })
			}

			return pages
		}
	},
	methods: {
		getField(name) {
			const f = fields[name]
			return Number(this.$route.query[f.key]) || f.default
		},
		getPage(page) {
			// eslint-disable-next-line no-unused-vars
			const { _start, ...query } = this.$route.query
			const target = (page - 1) * this.limit
			if (target) query._start = target
			else delete query.start

			return {
				name: this.$route.name,
				query
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@use "sass:math";
@import '~scss/vars';
@import '~scss/layout';
@import '~scss/components';

.pagination {
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0.75rem 0 -1rem;
}

.pages {
	display: flex;
	&__sq {
		@include square(1.75rem);
		@extend %flex-center;
		position: relative;
		&:focus {
			@extend %focus;
		}
	}
}

.page,
.arrow {
	outline: none;
}

.page {
	border-radius: 50%;
	margin: 0 -0.125rem;

	&--dot::after {
		@extend %pseudo;
		$side: 0.25rem;
		top: calc(50% - #{math.div($side, 2)});
		left: calc(50% - #{math.div($side, 2)});
		width: $side;
		height: $side;
		background-color: $black;
		border-radius: 50%;
	}

	&--active {
		color: $black;
	}

	&--link {
		text-decoration: none;
		&:hover,
		&:focus {
			background-color: $grey-95;
		}
		&:active {
			background-color: $grey-90;
		}
	}
}

.arrow {
	border-radius: 50%;
	&::after {
		@extend %pseudo;
		width: 0.4rem;
		height: 0.4rem;
		border: 0.125rem solid $grey-90;
		border-top: none;
		border-right: none;
		transform-origin: center;
		transform: translateX(0.1rem) rotate(45deg);
	}
	&--next {
		transform: rotate(0.5turn);
	}
	&--enable {
		&::after {
			border-color: $blue-73;
		}
		&:hover,
		&:focus {
			background-color: $grey-95;
		}
		&:active {
			background-color: $grey-90;
		}
	}
}
</style>
